import React, { useState } from 'react'
import { Terms } from '../shared/legacy/legal/terms'
import { AuthConnection, AuthHead, AuthNotice, AuthPage } from '../shared/sso-workspace/auth-card'
import { ErrorReference } from '../shared/error/error-reference'

// The page someone sees the first time they sign in through a single sign-on
// connection: /open/register (a new EducationXR account for the email the
// connection sent) and /open/link (that email already has an account; the
// connection is being linked to it). Both pages own their API calls and
// redirects (open-register.js, open-link.js); this is only what they show.
//
// Emails and connection names render exactly as they arrived -- never
// capitalized -- and the copy is sentence case.

const COPY = {
  register: {
    title: 'Create your EducationXR account',
    lede: (email, name) => <>We'll create an EducationXR account for <Email value={email} />. You'll sign in with {name} from now on.</>,
    submit: 'Create account'
  },
  link: {
    title: 'Link your EducationXR account',
    lede: (email, name) => <>We'll link {name} to your EducationXR account <Email value={email} />. You'll sign in with {name} from now on.</>,
    submit: 'Link account'
  }
}

const Email = ({ value }) =>
  value ? <span className="sa-email sw-mono" data-testid="sa-email">{value}</span> : <>your email</>

// AlertError's rules for what an API error carries, as plain lines.
const errorLines = error => {
  if (!error) return []
  if (Array.isArray(error)) return error.map(String)
  if (typeof error === 'object') return error.message ? [String(error.message)] : []
  return String(error).split('\n').filter(Boolean)
}

export const OpenAccountConsent = ({ mode, details, error, onSubmit, onCancel }) => {
  const copy = COPY[mode] || COPY.register
  const [agreed, setAgreed] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [busy, setBusy] = useState(false)

  const form = (details && details.form) || {}
  const connection = form.displayName || form.provider
  const name = connection ? <strong className="sa-name">{connection}</strong> : 'your organization\'s sign-in'
  const errors = errorLines(error)

  const handleSubmit = async () => {
    if (!agreed || busy) return
    setBusy(true)
    try {
      await onSubmit()
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthPage labelledBy="open-account-title">
      {connection && <AuthConnection prefix="Signing in with" providerLink={{ displayName: connection, iconSVG: form.iconSVG }} />}

      <AuthHead id="open-account-title" title={copy.title} lede={copy.lede(form.email, name)} />

      <AuthNotice tone="warn">
        <p>Only continue if you expected to sign in with {name}.</p>
      </AuthNotice>

      {errors.length > 0 &&
        <AuthNotice tone="bad" role="alert">
          {errors.map((line, i) => <p key={i}>{line}</p>)}
          <ErrorReference error={error} />
        </AuthNotice>}

      <div className="sa-terms">
        <div className="form-check sa-check">
          <input className="form-check-input" id="sa-terms-check" type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
          <label className="form-check-label" htmlFor="sa-terms-check">I agree to the terms and conditions</label>
        </div>
        <button
          type="button"
          className="btn btn-link sa-terms-toggle"
          aria-expanded={showTerms}
          aria-controls="sa-terms-body"
          onClick={() => setShowTerms(!showTerms)}
        >
          {showTerms ? 'Hide the terms and conditions' : 'Read the terms and conditions'}
        </button>
        {showTerms &&
          <div className="sa-terms-body" id="sa-terms-body" tabIndex={0} role="region" aria-label="Terms and conditions">
            <Terms />
          </div>}
      </div>

      <div className="sa-actions">
        <button
          type="button"
          className="btn sw-btn-primary sa-block"
          disabled={!agreed || busy}
          aria-describedby={agreed ? undefined : 'sa-terms-hint'}
          onClick={handleSubmit}
        >
          {copy.submit}
        </button>
        {!agreed && <p className="sa-hint" id="sa-terms-hint">Agree to the terms and conditions to continue.</p>}
        <button type="button" className="btn sw-btn sa-block" onClick={onCancel}>Cancel</button>
      </div>
    </AuthPage>
  )
}
